import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// Three-state theme. 'system' tracks the OS preference and updates live when
// the OS-level preference changes (rare but it happens). Persisted choice
// lives in localStorage so the next page load picks up where the user left
// off, with a tiny inline script in index.html (added by the host page) that
// avoids the flash-of-wrong-theme — but the provider also re-applies on
// mount as a safety net.

export type ThemeChoice = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  /** What the user chose. Use this to render the toggle UI. */
  choice: ThemeChoice;
  /** What's actually applied right now (resolves 'system' to 'light'/'dark'). */
  resolved: 'light' | 'dark';
  setChoice: (next: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'cyber-aware:theme';

function readStored(): ThemeChoice {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // localStorage may throw in private mode / locked-down browsers — fall
    // back to system silently.
  }
  return 'system';
}

function readSystem(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function apply(resolved: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  // Tailwind's `darkMode: ['class']` reads `.dark` on the element with
  // `dark:` utilities — applying to <html> covers the whole tree including
  // portals (dropdowns, dialogs).
  if (resolved === 'dark') document.documentElement.classList.add('dark');
  else document.documentElement.classList.remove('dark');
  // Helpful for native form controls (date pickers, scrollbars).
  document.documentElement.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readStored());
  const [systemPref, setSystemPref] = useState<'light' | 'dark'>(() => readSystem());

  // Live-update when the OS preference changes (e.g., scheduled
  // dark mode at sunset on macOS).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemPref(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolved: 'light' | 'dark' = choice === 'system' ? systemPref : choice;

  // Apply on mount + every time it changes.
  useEffect(() => {
    apply(resolved);
  }, [resolved]);

  const setChoice = (next: ThemeChoice) => {
    setChoiceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // see readStored
    }
  };

  return (
    <ThemeContext.Provider value={{ choice, resolved, setChoice }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
